import React, { useState } from 'react';
import cx from 'classnames';

import { isUndef, isFunction } from '@/utils';

import Text from '../Text';

export type AccordionItemProps = {
  id?: string;
  name?: string;
  label: React.ReactNode | string;
  openLabel?: React.ReactNode | string;
  icon?: React.ReactNode;
  expanded?: boolean;
  children?: React.ReactNode;
  onToggle?(newExpanded: boolean): void;
  className?: string;
  showToggleIcon?: boolean;
  labelPosition?: 'center' | 'left';
  showBottonCloseButton?: boolean;
};

const AccordionItem = ({
  id,
  name,
  label,
  openLabel,
  children,
  icon,
  expanded: expandedProp,
  onToggle,
  className,
  showToggleIcon = true,
  labelPosition = 'left',
  showBottonCloseButton = false,
}: AccordionItemProps) => {
  const [expandedFallback, setExpandedFallback] = useState(false);
  const expanded = expandedProp ?? expandedFallback;

  const classNames = cx(`relative flex flex-col`, className, {
    'pb-2': expanded,
    'flex-col-reverse': showBottonCloseButton && expanded,
  });

  const classeLabelPosition =
    labelPosition === 'center' ? 'justify-center' : 'justify-between';

  return (
    <div className={classNames}>
      <button
        name={name}
        type='button'
        className={`flex h-full w-full cursor-pointer items-center px-4 py-2 text-left text-tim-primary focus:outline-none ${classeLabelPosition}`}
        aria-controls={id}
        aria-expanded={expanded}
        onClick={() => {
          const newExpanded = !expanded;

          if (isUndef(expandedProp)) {
            setExpandedFallback(newExpanded);
          }

          if (isFunction(onToggle)) {
            onToggle(newExpanded);
          }
        }}
      >
        <div className='flex items-center'>
          {icon || <div className='h-4 w-4' />}
          <Text as='div' className='pl-2 font-medium'>
            {!expanded && openLabel ? openLabel : label}
          </Text>
        </div>
        {showToggleIcon ? (
          <Text
            as='div'
            kind='gray'
            size='xl'
            className='pb-1 text-right leading-none'
          >
            {expanded ? `-` : `+`}
          </Text>
        ) : null}
      </button>
      <div id={id} className={`w-full pb-1 ${expanded ? 'block' : 'hidden'}`}>
        {children}
      </div>
    </div>
  );
};

export default AccordionItem;
